import { RotateCcw, RotateCw, ScanSearch } from 'lucide-react';
import { forwardRef, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DEFAULT_ENHANCEMENTS,
  MAX_FINE_ROTATION_DEG,
  type RenderParams,
} from '@/tools/image-engine';
import { CornerEditor } from '@/tools/images/CornerEditor';
import { LivePreview } from '@/tools/images/LivePreview';
import { useImageSession } from '@/tools/images/use-image-session';

export interface RectifiedImage {
  readonly dataUrl: string;
  readonly base64Data: string;
  readonly mediaType: 'image/jpeg';
}

export interface DocumentSlotHandle {
  /** The straightened JPEG for the current corners and rotation, or null when empty. */
  getRectifiedImage: () => Promise<RectifiedImage | null>;
  hasImage: () => boolean;
}

function RectifyControls({
  session,
  rotation,
  onRotation,
}: {
  session: ReturnType<typeof useImageSession>;
  rotation: number;
  onRotation: (degrees: number) => void;
}) {
  const { t } = useTranslation(['identity-documents', 'tools']);
  const uid = useId();
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('tools:images.straighten.rotateLeft')}
          onClick={() => void session.rotateQuarter(-1)}
        >
          <RotateCcw />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('tools:images.straighten.rotateRight')}
          onClick={() => void session.rotateQuarter(1)}
        >
          <RotateCw />
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => void session.detect()}>
          <ScanSearch aria-hidden="true" />
          {t('tools:images.straighten.detect')}
        </Button>
      </div>
      <label
        htmlFor={`${uid}-fine`}
        className="flex items-center gap-2 text-xs text-muted-foreground"
      >
        {t('tools:images.straighten.fine')}
        <input
          id={`${uid}-fine`}
          type="range"
          min={-MAX_FINE_ROTATION_DEG}
          max={MAX_FINE_ROTATION_DEG}
          step={0.5}
          value={rotation}
          onChange={(e) => onRotation(Number(e.target.value))}
          className="h-11 w-32 accent-[var(--color-brand)]"
        />
        <span className="tabular-nums text-foreground">{rotation}°</span>
      </label>
    </>
  );
}

/**
 * One side of a document (front or back), straightened with the Immagini tool's parts: detected
 * corners, 44 px handles with a loupe, 90° and fine rotation (#387, decision 98 «its Raddrizza is
 * the Immagini one»). The export applies the document corrections the old rectifier defaulted to
 * and never anything AI: the transcription reads this image only (D19, point 4).
 */
export const DocumentSlot = forwardRef<
  DocumentSlotHandle,
  { readonly badge: string; readonly hint: string }
>(function DocumentSlot({ badge, hint }, ref) {
  const { t } = useTranslation(['identity-documents', 'tools']);
  const session = useImageSession();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const [readError, setReadError] = useState(false);

  const params: RenderParams | null = useMemo(
    () =>
      session.corners
        ? { corners: session.corners, rotation, settings: DEFAULT_ENHANCEMENTS }
        : null,
    [session.corners, rotation],
  );

  useImperativeHandle(ref, () => ({
    hasImage: () => session.image !== null,
    getRectifiedImage: async () => {
      const dataUrl = await session.exportJpeg({ rotation, settings: DEFAULT_ENHANCEMENTS });
      return dataUrl
        ? { dataUrl, base64Data: dataUrl.split(',')[1] ?? '', mediaType: 'image/jpeg' }
        : null;
    },
  }));

  return (
    <div className="min-w-0 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{badge}</span>
        <Button variant="outline" size="sm" type="button" onClick={() => inputRef.current?.click()}>
          {session.image
            ? t('cornerRectifier.changeImageButton')
            : t('cornerRectifier.chooseImageButton')}
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setReadError(false);
          setRotation(0);
          session.load(file).catch(() => setReadError(true));
        }}
      />
      {readError && (
        <p role="alert" className="text-sm text-destructive">
          {t('cornerRectifier.imageReadError')}
        </p>
      )}
      {!session.image || !session.corners ? (
        <div className="flex min-h-40 items-center justify-center rounded border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          {t('cornerRectifier.noImageMessage')}
        </div>
      ) : (
        <>
          <CornerEditor
            source={session.image.source}
            corners={session.corners}
            onChange={(next, final) => {
              if (final) session.setCorners(next);
            }}
          />
          <p className="text-xs text-muted-foreground">{hint}</p>
          <RectifyControls session={session} rotation={rotation} onRotation={setRotation} />
          <LivePreview
            engine={session.engine}
            params={params}
            label={t('cornerRectifier.previewLabel')}
            className="block h-auto w-full rounded border border-border"
          />
        </>
      )}
    </div>
  );
});
